
import { supabase } from '@/lib/supabase';
import imageCompression from 'browser-image-compression';

// Upload an image to Cloudinary with compression
export const uploadToCloudinary = async (file: File): Promise<string | null> => {
  try {
    // Compress the image before uploading if it's too large
    let fileToUpload = file;
    if (file.size > 10 * 1024 * 1024) { // If larger than 10MB
      console.log('Image is very large, compressing before Cloudinary upload...');
      const options = {
        maxSizeMB: 10,
        maxWidthOrHeight: 3840, // 4K resolution
        useWebWorker: true
      };
      
      fileToUpload = await imageCompression(file, options);
      console.log('Original file size:', file.size / 1024 / 1024, 'MB');
      console.log('Compressed file size:', fileToUpload.size / 1024 / 1024, 'MB');
    }

    // Convert File to base64
    const base64Data = await fileToBase64(fileToUpload);
    if (!base64Data) {
      throw new Error('Could not convert file to base64');
    }

    // Upload to Cloudinary via Supabase Edge Function
    const { data, error } = await supabase.functions.invoke('cloudinary-upload', {
      method: 'POST',
      body: JSON.stringify({
        file: base64Data,
        folder: 'hero-images',
        transformation: {
          quality: 'auto',
          fetch_format: 'auto'
        }
      })
    });

    if (error) {
      throw error;
    }

    return data?.secure_url || null;
  } catch (error) {
    console.error('Error uploading to Cloudinary:', error);
    return null;
  }
};

// Helper function to convert File to base64
const fileToBase64 = (file: File): Promise<string | null> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = error => {
      console.error('Error converting file to base64:', error);
      reject(null);
    };
  });
};

// Generate responsive image URL with Cloudinary transformations
export const getResponsiveImageUrl = (url: string, width: number = 1200): string => {
  if (!url || !url.includes('cloudinary.com')) {
    return url;
  }

  // Parse the URL to insert transformations
  try {
    const parsedUrl = new URL(url);
    const pathParts = parsedUrl.pathname.split('/');
    
    // Find where the version or upload part is
    const uploadIndex = pathParts.findIndex(part => 
      part === 'upload' || /v\d+/.test(part)
    );
    
    if (uploadIndex >= 0) {
      // Insert transformation
      pathParts.splice(uploadIndex + 1, 0, `c_fill,w_${width},q_auto,f_auto`);
      parsedUrl.pathname = pathParts.join('/');
      return parsedUrl.toString();
    }

    return url;
  } catch (e) {
    console.error('Error parsing Cloudinary URL:', e);
    return url;
  }
};
